import React, { useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ColunaOrdenavelUsina,
  DirecaoOrdenacao,
  OrdenacaoUsinas,
  UsinaEolicaDenormalizada,
} from 'helper/windx';
import LinhaUsina from '../LinhaUsina';

interface TabelaUsinasProps {
  usinas: UsinaEolicaDenormalizada[];
  isSelecionada: (id: number) => boolean;
  onToggle: (id: number) => void;
  onSelecionarVisiveis: (ids: number[]) => void;
  onDesselecionarVisiveis: (ids: number[]) => void;
  ordenacao: OrdenacaoUsinas;
  onOrdenacaoChange: (o: OrdenacaoUsinas) => void;
}

const ALTURA_LINHA = 36;
const OVERSCAN = 8;

interface ColunaDef {
  chave: ColunaOrdenavelUsina;
  label: string;
  className: string;
}

const COLUNAS: ColunaDef[] = [
  { chave: 'codigo', label: 'CÓDIGO', className: 'windx-seletor-usinas__cell--codigo' },
  { chave: 'nome', label: 'NOME', className: 'windx-seletor-usinas__cell--nome' },
  { chave: 'nucleo_ceg', label: 'CEG', className: 'windx-seletor-usinas__cell--ceg' },
  { chave: 'potencia', label: 'POT MW', className: 'windx-seletor-usinas__cell--potencia' },
  { chave: 'uf_sigla', label: 'UF', className: 'windx-seletor-usinas__cell--uf' },
  { chave: 'subsistema_sm_sigla', label: 'SUB.', className: 'windx-seletor-usinas__cell--sub' },
];

function proximaDirecao(atual: DirecaoOrdenacao): DirecaoOrdenacao {
  return atual === 'asc' ? 'desc' : 'asc';
}

function ariaSort(
  ordenacao: OrdenacaoUsinas,
  chave: ColunaOrdenavelUsina,
): 'ascending' | 'descending' | 'none' {
  if (ordenacao.coluna !== chave) return 'none';
  return ordenacao.direcao === 'asc' ? 'ascending' : 'descending';
}

/**
 * Tabela virtualizada de usinas eolicas.
 *
 * Renderiza ~20 linhas (visiveis + overscan) num container de 480px,
 * independente do tamanho do array filtrado. Cada `LinhaUsina` recebe
 * `translateY` calculado pelo `@tanstack/react-virtual` — o DOM mantem
 * referencias absolutas dentro de um wrapper de altura total.
 *
 * Header com checkbox "todos visiveis" com estado tri-state via
 * `indeterminate` (DOM-only, sem prop React equivalente).
 */
export default function TabelaUsinas({
  usinas,
  isSelecionada,
  onToggle,
  onSelecionarVisiveis,
  onDesselecionarVisiveis,
  ordenacao,
  onOrdenacaoChange,
}: TabelaUsinasProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const checkboxTodosRef = useRef<HTMLInputElement | null>(null);

  const virtualizer = useVirtualizer({
    count: usinas.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ALTURA_LINHA,
    overscan: OVERSCAN,
  });

  const idsVisiveis = usinas.map((u) => u.id);
  const selecionadasVisiveis = idsVisiveis.filter(isSelecionada).length;
  const todasVisiveisSelecionadas = (
    idsVisiveis.length > 0 && selecionadasVisiveis === idsVisiveis.length
  );
  const parcialVisivel = (
    selecionadasVisiveis > 0 && selecionadasVisiveis < idsVisiveis.length
  );

  // `indeterminate` so e settavel via DOM ref — React nao expoe prop.
  useEffect(() => {
    if (checkboxTodosRef.current) {
      checkboxTodosRef.current.indeterminate = parcialVisivel;
    }
  }, [parcialVisivel]);

  const onCheckTodos = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      onSelecionarVisiveis(idsVisiveis);
    } else {
      onDesselecionarVisiveis(idsVisiveis);
    }
  };

  const handleOrdenar = (chave: ColunaOrdenavelUsina) => {
    if (ordenacao.coluna === chave) {
      onOrdenacaoChange({ coluna: chave, direcao: proximaDirecao(ordenacao.direcao) });
    } else {
      onOrdenacaoChange({ coluna: chave, direcao: 'asc' });
    }
  };

  const setaOrdenacao = (chave: ColunaOrdenavelUsina) => {
    if (ordenacao.coluna !== chave) return '';
    return ordenacao.direcao === 'asc' ? ' ↑' : ' ↓';
  };

  const totalSize = virtualizer.getTotalSize();
  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className="windx-seletor-usinas__tabela" role="table" aria-rowcount={usinas.length}>
      <div className="windx-seletor-usinas__tabela-head" role="row">
        <div className="windx-seletor-usinas__cell windx-seletor-usinas__cell--check" role="columnheader">
          <input
            ref={checkboxTodosRef}
            type="checkbox"
            className="form-check-input"
            checked={todasVisiveisSelecionadas}
            onChange={onCheckTodos}
            disabled={idsVisiveis.length === 0}
            aria-label="Selecionar todas as visíveis"
          />
        </div>
        {COLUNAS.map((col) => (
          <button
            key={col.chave}
            type="button"
            role="columnheader"
            aria-sort={ariaSort(ordenacao, col.chave)}
            className={`windx-seletor-usinas__cell ${col.className} windx-seletor-usinas__th`}
            onClick={() => handleOrdenar(col.chave)}
          >
            {col.label}
            {setaOrdenacao(col.chave)}
          </button>
        ))}
      </div>
      <div ref={scrollRef} className="windx-seletor-usinas__tabela-body">
        {usinas.length === 0 ? (
          <div className="windx-seletor-usinas__vazio">
            Nenhuma usina encontrada para os filtros aplicados.
          </div>
        ) : (
          <div style={{ height: `${totalSize}px`, position: 'relative' }}>
            {virtualItems.map((item) => {
              const usina = usinas[item.index];
              return (
                <LinhaUsina
                  key={usina.id}
                  usina={usina}
                  selecionada={isSelecionada(usina.id)}
                  onToggle={onToggle}
                  altura={ALTURA_LINHA}
                  translateY={item.start}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
